/**
 * URLs de las paginas de categoria.
 *
 * Tienen que salir IGUAL que las arma el backend (CatalogoSeo.java): esas son las que van al
 * sitemap y al canonical, y si el SPA linkeara otra forma Google veria dos URLs por pagina.
 *
 *   /repuestos/{tipo}                    un tipo de repuesto
 *   /repuestos/{tipo}/{marca}            ese tipo, para una marca
 *   /autopartes-usadas/{marca}           todo lo de una marca
 *   /autopartes-usadas/{marca}/{modelo}  todo lo de un modelo
 *
 * Cada segmento es `{id}-{slug}`: se lee el id y el texto es decorativo.
 */

const slug = (texto) =>
    String(texto || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-+|-+$)/g, "")
        .slice(0, 200);

const segmento = (item) => {
    const texto = slug(item?.nombre);
    return texto ? `${item.id}-${texto}` : String(item.id);
};

const idDe = (seg) => {
    const match = /^(\d+)/.exec(seg || "");
    return match ? Number(match[1]) : undefined;
};

/** La "baja" es el auto entero, no un tipo de repuesto: no tiene pagina propia (igual que en el back). */
const TIPO_REPUESTO_BAJA = 34;

/**
 * La pagina que corresponde EXACTAMENTE a esa combinacion, o undefined si no hay una.
 *
 * Estricta a proposito: tipo + marca + modelo, o la baja, no tienen pagina, y devolver la mas
 * parecida dejaria la URL diciendo una cosa con otros filtros en pantalla (y al recargar se
 * perderia el filtro que no entra en la URL).
 */
export const urlCategoria = ({tipoRepuesto, marca, modelo} = {}) => {
    if (tipoRepuesto?.id) {
        if (Number(tipoRepuesto.id) === TIPO_REPUESTO_BAJA || modelo?.id) return undefined;

        return `/repuestos/${segmento(tipoRepuesto)}` + (marca?.id ? `/${segmento(marca)}` : "");
    }

    if (marca?.id) {
        return `/autopartes-usadas/${segmento(marca)}` + (modelo?.id ? `/${segmento(modelo)}` : "");
    }

    return undefined;
};

/**
 * Los ids de la categoria de la URL actual, o undefined si no se esta en una.
 *
 * @param path  `match.path` de react-router (el patron de la ruta, no la URL).
 */
export const categoriaDeLaRuta = (path, params) => {
    if (!path || !params) return undefined;

    if (path.startsWith("/repuestos/")) {
        const tipoRepuestoId = idDe(params.tipo);
        if (!tipoRepuestoId) return undefined;

        return {tipoRepuestoId, marcaId: idDe(params.marca)};
    }

    if (path.startsWith("/autopartes-usadas/")) {
        const marcaId = idDe(params.marca);
        if (!marcaId) return undefined;

        return {marcaId, modeloId: idDe(params.modelo)};
    }

    return undefined;
};

/**
 * Titulos y textos de una categoria, con los nombres ya resueltos. Mismo texto que el prerender del
 * backend, para que la pestaña, el resultado de Google y lo que se comparte digan lo mismo.
 */
export const textosCategoria = ({tipoRepuesto, marca, modelo}) => {
    const tipo = tipoRepuesto?.nombre?.trim();
    const mar = marca?.nombre?.trim();
    const mod = modelo?.nombre?.trim();

    if (tipo) {
        const h1 = mar ? `${tipo} ${mar}` : tipo;

        return {
            h1,
            titulo: `${h1} — repuestos usados originales`,
            descripcion: `${mar ? `${tipo} para ${mar}` : tipo}: repuestos usados originales de desarmadero. `
                + "Compará precios de vendedores verificados y comprá con envío a todo el país.",
        };
    }

    if (mar) {
        const h1 = `Repuestos usados ${mod ? `${mar} ${mod}` : mar}`;

        return {
            h1,
            titulo: `${h1} — autopartes originales de desarme`,
            descripcion: `Autopartes usadas originales para ${mod ? `${mar} ${mod}` : mar}, de desarmaderos `
                + "y vendedores verificados. Envíos a todo el país.",
        };
    }

    return undefined;
};
